import api from '../common/services/api';
import bootstrapSite from '../app/bootstrapSite';
import { trackAddProductToBasketEvent } from '../utilities/trackAddToBasketEvent';
import getFromDataLayer from '../utilities/getFromDataLayer';
import { createCarousel } from '../common/components/carousels';

bootstrapSite();

const userLoggedIn = getFromDataLayer(window.dataLayer, 'user')?.loggedIn;

const isProductFormInputValid = (value) => {
    const pattern = /^(?!.*[<>])[\p{Letter}\p{Number}\p{Mark}\s\p{Punctuation}\p{Currency_Symbol}@!?:;&%+=_*…—'-^`]*$/u;
    return pattern.test(value);
};

const cleanNormalizedString = (str) => {
    return str
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .replace(/[øæßł]/gi, '');
};

const LETTER_LIMIT = 250;

const updateCount = (value) => {
    const remainingChars = LETTER_LIMIT - value.length;

    const charsElem = document.getElementById('chars');
    
    if (charsElem) {
        charsElem.textContent = remainingChars;
    }

    const limitElem = document.getElementById('character-limit');

    if (limitElem) {
        if (remainingChars <= 0) {
            limitElem.setAttribute('role', 'alert');
            limitElem.setAttribute('aria-live', 'assertive');
        } else {
            limitElem.removeAttribute('role');
            limitElem.removeAttribute('aria-live');
        }
    }

    const cleanedText = cleanNormalizedString(value);

    const eVoucherMsg = document.getElementById('product_evoucher_gift_message');
    
    if (eVoucherMsg) {
        eVoucherMsg.value = cleanedText;
    }

    const giftMessageInput = document.getElementById('product_gift_message');
    
    if (giftMessageInput) {
        giftMessageInput.value = cleanedText;
    }
};

const ROW_LIMIT = 6;

const updateRowCount = (target) => {
    const rows = Math.max(0, target.value.split('\n').length - 1);

    const rowsUsedElem = document.getElementById('rows-used');
    const rowLimitElem = document.getElementById('row-limit');

    if (ROW_LIMIT - rows <= 1) {
        if (rowsUsedElem) {
            rowsUsedElem.textContent = 'Final row';
        }

        if (rowLimitElem) {
            rowLimitElem.setAttribute('role', 'alert');
            rowLimitElem.setAttribute('aria-live', 'assertive');
        }
    } else {
        if (rowsUsedElem) {
            rowsUsedElem.textContent = `${ROW_LIMIT - rows} rows left`;
        }

        if (rowLimitElem) {
            rowLimitElem.removeAttribute('role');
            rowLimitElem.removeAttribute('aria-live');
        }
    }

    if (rows >= ROW_LIMIT) {
        target.value = target.value.split('\n').slice(0, ROW_LIMIT).join('\n');
    }
};

const renderSelectOptions = (selectElem, optionsData) => {
    const fragment = document.createDocumentFragment();
    
    optionsData.forEach(({ value, text, dataset }) => {
        const option = document.createElement('option');
        option.value = value;
        option.textContent = text;
        
        if (dataset) {
            Object.entries(dataset).forEach(([k, v]) => {
                option.dataset[k] = v;
            });
        }
        fragment.appendChild(option);
    });
    
    selectElem.textContent = ''; 
    selectElem.appendChild(fragment);
};

const clearInputError = (parentElem) => {
    parentElem.querySelector('.error-message')?.remove();
};

const showInputError = (parentElem, message) => {
    parentElem.querySelector('.error-message')?.remove();
        
    parentElem.insertAdjacentHTML('beforeend', `<p class="error-message">${message}</p>`);
};

const prodAmountElem = document.getElementById('shop-product-info-amount');
const prodId = prodAmountElem ? prodAmountElem.getAttribute('data-id') : null;

const productCurrencies = document.getElementById('product_currencies');

document.addEventListener('click', (event) => {
    if (event.target.matches('.gift-message-radio-wrapper input[type="radio"]')) {
        const giftMessageWrapperElement = document.querySelector('.gift-message-input-wrapper');

        if (event.target.value === '0') {
            giftMessageWrapperElement.classList.add('hidden');
        } else {
            giftMessageWrapperElement.classList.remove('hidden');
        }
    }

    if (event.target.matches('.card-destination-wrapper input[type="radio"]')) {
        const recipientDetails = document.getElementById('shopRecipientDetails');

        if (event.target.value === '0') {
            if (recipientDetails) recipientDetails.classList.remove('hidden');
        } else {
            if (recipientDetails) recipientDetails.classList.add('hidden');

            document.querySelectorAll('.recipent-details-wrapper input, .recipent-details-wrapper textarea')
                .forEach((input) => { input.value = ''; });
        }
    }
});

['product_gift_message', 'product_evoucher_gift_message'].forEach((selector) => {
    const elem = document.getElementById(selector);

    if (elem) {
        ['keyup', 'keydown', 'paste'].forEach((evt) => {
            elem.addEventListener(evt, (e) => {
                updateCount(e.target.value);
                updateRowCount(e.target);
            });
        });

        ['blur', 'keyup'].forEach((evt) => {
            elem.addEventListener(evt, (e) => {
                if (isProductFormInputValid(e.target.value)) {
                    clearInputError(e.target.parentElement);
                }
            });
        });
    }
});

const productPrices = document.getElementById('product_prices');

if (productCurrencies) {
    productCurrencies.addEventListener('change', async (event) => {
        const currentTarget = event.currentTarget;
        const currentCurrency = currentTarget.value;

        try {
            const data = await api.getShopPrices({ id: prodId, currency: currentCurrency });

            if (data?.prices) {
                const options = Object.entries(data.prices).map(([key, priceString]) => {
                    const [basePrice, subItemPrice, symbol] = priceString.split(';');
                    const subPriceNum = parseFloat(subItemPrice) || 0;

                    if (subPriceNum > 0) {
                        return {
                            value: key,
                            text: basePrice,
                            dataset: { 
                                attribute_total: Number(basePrice) + subPriceNum, 
                                attribute_currencySymbol: symbol 
                            }
                        };
                    }

                    return { value: key, text: basePrice };
                });

                if (productPrices) {
                    renderSelectOptions(productPrices, options);
                }
            }
        } catch (err) {
            console.error('Failed to update shop prices:', err);
        }
    });
}

const productAdd = document.getElementById('add-to-basket-button');

productAdd.addEventListener('click', (event) => {
    const form = event.currentTarget.closest('form');

    const giftMessageSelected = document.getElementById('product_is_gift_1')?.checked;
    const selectedToMyinbox = document.getElementById('product_to_member_inbox_0')?.checked;
    const membershipPlans = document.getElementById('product_membership_plans');

    let hasErrors = document.querySelectorAll('.form-error').length > 0;

    if (giftMessageSelected) {
        event.preventDefault();

        const productGiftMessage = document.getElementById('product_gift_message');

        if (productGiftMessage) {
            if(!productGiftMessage.value) {
                hasErrors = true;

                showInputError(productGiftMessage.parentElement, 'This is a required field');
            }

            if (!isProductFormInputValid(productGiftMessage.value)) {
                hasErrors = true;

                showInputError(productGiftMessage.parentElement, 'One or more characters is not allowed.');
            }
            
            if (!hasErrors) {
                form?.submit();
            }
        }
    }

    const selectedSendToRecipient = document.getElementById('product_to_member_inbox_1')?.checked;

    if (selectedSendToRecipient) {
        event.preventDefault();

        document.querySelectorAll('.recipent-details-wrapper input').forEach((input) => {
            if (input.value.trim() === '') {
                hasErrors = true;
                showInputError(input.parentElement, 'This is a required field');
            }
        });

        if (!hasErrors) {
            const recipientEmail = document.getElementById('product_shop_recipients_email');
            const confirmRecipientEmail = document.getElementById('product_shop_confirm_recipients_email');

            const recipientVal = recipientEmail?.value.trim() || '';
            const confirmVal = confirmRecipientEmail?.value.trim() || '';
            const pattern = recipientEmail?.getAttribute('pattern') || '';

            if (recipientEmail && !new RegExp(pattern).test(recipientVal)) {
                hasErrors = true;
                showInputError(recipientEmail.parentElement, 'Please enter a valid email address');
            }

            if (confirmRecipientEmail && recipientVal !== confirmVal) {
                hasErrors = true;
                showInputError(confirmRecipientEmail.parentElement, 'Your emails do not match, please try again');
            }
        }

        if (!hasErrors) {
            const giftMessage = document.getElementById('product_evoucher_gift_message');

            if (giftMessage && !isProductFormInputValid(giftMessage.value)) {
                hasErrors = true;
                showInputError(giftMessage.parentElement, 'One or more characters is not allowed.');
            }

            document.querySelectorAll('.recipent-details-wrapper input').forEach((input) => {
                if (!isProductFormInputValid(input.value)) {
                    hasErrors = true;
                    showInputError(input.parentElement, 'One or more characters is not allowed.');
                }
            });
        }

        if (!hasErrors) {
            trackAddProductToBasketEvent(event.target);
            form?.submit();
        }
    } else if (selectedToMyinbox) {
        const recipientDetails = document.getElementById('shopRecipientDetails');

        if (recipientDetails) {
            recipientDetails.classList.add('hidden');
        }

        trackAddProductToBasketEvent(event.target);

        form?.submit();
    } else if (!membershipPlans || userLoggedIn) {
        trackAddProductToBasketEvent(event.target);
    }
});

document.querySelectorAll('.recipent-details-wrapper input').forEach((input) => {
    ['blur', 'keyup'].forEach((evt) => {
        input.addEventListener(evt, (e) => {
            if (e.target.value !== '' && isProductFormInputValid(e.target.value)) {
                clearInputError(e.target.parentElement);
            }
        });
    });
});

const memberInboxRadio = document.getElementById('product_to_member_inbox_0');
const recipientInboxRadio = document.getElementById('product_to_member_inbox_1');
const recipentSection = document.querySelector('.recipient-validation-message');

if (recipientInboxRadio && recipentSection) {
    document.getElementById('product_quantity')?.addEventListener('change', (event) => {
        const quantity = event.currentTarget.value;

        if (quantity > 1) {
            memberInboxRadio.click();

            recipientInboxRadio.disabled = true;
            recipentSection.classList.remove('hidden');
        } else {
            recipientInboxRadio.disabled = false;
            recipentSection.classList.add('hidden')
        }
    });
}

createCarousel({
    HTMLWrapper: document.querySelector('.product-cards-gallery'),
    cols: {
        xs: 1.2,
        sm: 1.2,
        md: 2,
        lg: 3,
        xl: 3,
        xxl: 3
    },
    padding: 'var(--product-card-spacing)',
})